import axios from "axios";
import {createProgressListener, ProgressListener} from "@/utils/progressListener";
import ProgressSmoother, {ProgressSmootherConfig} from "@/utils/ProgressSmoother";
import {PersistedExtractionJob, TranslationJobStatus, TranslationResult, TranslationSummary} from "@/types";
import InsufficientCreditsException from "@/exceptions/InsufficientCreditsException";

const progressConfig: ProgressSmootherConfig = {
    maxValue: 100,
    averageTimeBetweenValues: 1000 * 25,
    averageValueIncreaseDelta: 10,
    delayUntilFirstValue: 1000 * 25,
    firstValue: 35,
}

export class RefuseToTranslateError extends Error {
    constructor() {
        super("Translation declined. Content may be inappropriate.");
    }
}

interface TranslateItem {
    extractionJobId: number
    id: string
    language: string
}

type TranslationTracker = {
    progressTracker: Map<string, number>;
    failedJobs: Map<string, Error>;
    translationResults: Map<number, TranslationResult>;
    totalJobs: number;
};

export type TranslationListenerConfig = {
    jobId: string;
    videoDuration?: number;
    startedAt?: number;
    onUpdate: (progress: number) => void;
    onComplete: () => void;
    onError: (error: Error) => void;
};


export function listenForTranslationProgress(config: TranslationListenerConfig): ProgressListener {
    const smoothingConfig = makeSmoothingConfig(config.videoDuration ?? 3600);
    smoothingConfig.started_at = config.startedAt;
    const progressSmoother = new ProgressSmoother(smoothingConfig);

    return createProgressListener({
        jobId: config.jobId,
        progressSmoother,
        updatesPerSecond: 2,
        onProgress: config.onUpdate,
        onComplete: config.onComplete,
        onError: config.onError,
        requestInitialProgress: true
    });
}

export function translate(
    jobs: PersistedExtractionJob[],
    targetLanguages: string[],
    sourceLanguage: string | null,
    onProgress: (progress: number) => void,
): Promise<TranslationSummary> {
    return new Promise<TranslationSummary>(async (resolve, reject) => {
        try {
            const translateItems = await startTranslationJobs(jobs, targetLanguages, sourceLanguage);
            const tracker = createTranslationTracker(jobs, translateItems.length);
            const updateProgress = () => {
                const totalProgress = Array.from(tracker.progressTracker.values())
                    .reduce((sum, p) => sum + p, 0);
                onProgress(Math.min(totalProgress / tracker.totalJobs, 100));
            };

            const checkCompletion = () => {
                const completedCount = Array.from(tracker.progressTracker.values()).filter(p => p === 100).length;
                const finishedCount = completedCount + tracker.failedJobs.size;

                if (finishedCount === tracker.totalJobs) {
                    const results = Array.from(tracker.translationResults.values());
                    const hasFailures = results.some(r => r.failed.length > 0);
                    const hasSuccesses = results.some(r => r.successful.length > 0);

                    resolve({
                        results,
                        hasFailures,
                        hasSuccesses
                    });
                }
            };

            setupJobListeners(jobs, translateItems, tracker, updateProgress, checkCompletion);

        } catch (error) {
            handleApiError(error as any, reject);
        }
    });
}

export async function getTranslationJobStatus(jobId: number, language: string): Promise<TranslationJobStatus> {
    const url = route('subtitle.translationStatus', jobId);
    const res = await axios.get(url, {
        params: {language}
    });
    return res.data.status;
}

async function startTranslationJobs(
    jobs: PersistedExtractionJob[],
    targetLanguages: string[],
    sourceLanguage: string | null
): Promise<TranslateItem[]> {
    const apiRoute = route("subtitle.translate");
    const res = await axios.post(apiRoute, {
        jobs: jobs.map(job => job.id),
        targetLanguages: targetLanguages,
        sourceLanguage
    });
    return res.data;
}

function createTranslationTracker(jobs: PersistedExtractionJob[], totalJobs: number): TranslationTracker {
    const translationResults = new Map<number, TranslationResult>();

    jobs.forEach(job => {
        translationResults.set(job.id, {
            jobId: job.id,
            jobFileName: job.file_name,
            successful: [],
            failed: []
        });
    });

    return {
        progressTracker: new Map<string, number>(),
        failedJobs: new Map<string, Error>(),
        translationResults,
        totalJobs
    };
}

function setupJobListeners(
    jobs: PersistedExtractionJob[],
    translateItems: TranslateItem[],
    tracker: TranslationTracker,
    updateProgress: () => void,
    checkCompletion: () => void
) {
    translateItems.forEach(item => {
        const job = jobs.find(j => j.id === item.extractionJobId);
        if (!job) return;

        const smoothingConfig = makeSmoothingConfig(job.file_duration);
        const progressSmoother = new ProgressSmoother(smoothingConfig);

        const onItemProgress = (progress: number) => {
            tracker.progressTracker.set(item.id, progress);
            updateProgress();
        };

        const onItemComplete = () => {
            tracker.progressTracker.set(item.id, 100);

            const jobResult = tracker.translationResults.get(item.extractionJobId)!;
            jobResult.successful.push({ language: item.language });

            updateProgress();
            checkCompletion();
        };

        const onItemError = (error: Error) => {
            tracker.progressTracker.delete(item.id);
            tracker.failedJobs.set(item.id, error);

            const jobResult = tracker.translationResults.get(item.extractionJobId)!;
            jobResult.failed.push({
                language: item.language,
                error: error.message
            });

            updateProgress();
            checkCompletion();
        };

        createProgressListener({
            jobId: item.id,
            progressSmoother,
            updatesPerSecond: 1,
            onProgress: onItemProgress,
            onComplete: onItemComplete,
            onError: onItemError,
        });
    });
}

function handleApiError(error: any, reject: (reason?: any) => void) {
    if (error.response) {
        const data = error.response.data;
        if (data?.type === "insufficient_credits") {
            reject(new InsufficientCreditsException(data.balance));
            return;
        }
        const msg = data.message ? data.message : JSON.stringify(data);
        reject(new Error(msg));
    } else {
        reject(error);
    }
}

function makeSmoothingConfig(videoDuration: number): ProgressSmootherConfig {
    const hours = videoDuration / 3600;
    const config = {...progressConfig};
    const scaleFactor = Math.min(1 / hours, 1)
    config.firstValue *= scaleFactor;
    config.averageValueIncreaseDelta *= scaleFactor;
    return config;
}
